import { useQueryData } from "@src/hooks/useQueryData";
import { byCode, reloadAndReplaceCachedListItem } from "@src/hooks/queryListCache";
import {
    finishedGoodsReceiptsServices,
    type FinishedGoodsReceipt,
    type FinishedGoodsReceiptContent,
    type FinishedGoodsReceiptContentExtends,
    type FinishedGoodsReceiptExtends,
} from "../../../../api/finishedGoodsReceipts";

/** Связи карточки ВЗН УП для приемки СГП. */
const FINISHED_GOODS_RECEIPT_CARD_INCLUDES = {
    include: ["Sender", "SenderSection", "Receiver", "ReceiverSection", "Bo"] satisfies Array<
        keyof FinishedGoodsReceiptExtends
    >,
    includeBo: ["State"] satisfies Array<"State">,
} as const;

/** Связи строк ВЗН УП для приемки СГП. */
const FINISHED_GOODS_RECEIPT_CONTENT_INCLUDES = {
    include: ["LeaveCard", "Order", "ArrivalLot"] satisfies Array<keyof FinishedGoodsReceiptContentExtends>,
    includeLeaveCard: ["Stockobj", "Measure"] satisfies Array<"Stockobj" | "Measure">,
} as const;

/**
 * Ключ query состава ВЗН УП для приемки СГП.
 * @param inplantCode код ВЗН УП
 * @param divisionCode код склада-получателя
 */
export function getStockFinishedGoodsReceiptContentsQueryKey(inplantCode: number, divisionCode: number) {
    return ["stock", "finishedGoodsReceipt", inplantCode, "contents", divisionCode] as const;
}

/** Загружает карточку ВЗН УП для приемки СГП. */
export function useStockFinishedGoodsReceiptCardQuery(inplantCode: number) {
    return useQueryData<FinishedGoodsReceipt | null>({
        queryKey: ["stock", "finishedGoodsReceipt", inplantCode, "card"],
        queryFn: ({ signal }) =>
            finishedGoodsReceiptsServices.loadOne(
                {
                    flt: { Codes: [inplantCode] },
                    ...FINISHED_GOODS_RECEIPT_CARD_INCLUDES,
                },
                { signal },
            ),
        enabled: inplantCode > 0,
        meta: { errorSource: "stock.finishedGoodsReceipt.card" },
    });
}

/** Результат загрузки строк ВЗН УП для приемки СГП. */
export interface UseStockFinishedGoodsReceiptContentsQueryResult {
    /** Строки ВЗН УП. */
    content: FinishedGoodsReceiptContent[];
    /** Признак фоновой загрузки. */
    isFetching: boolean;
    /** Перезагружает строки ВЗН УП. */
    refetch: () => Promise<unknown>;
    /**
     * Перезагружает одну строку ВЗН УП и заменяет её в query cache.
     * @param contentCode код строки состава ВЗН УП
     */
    refetchContentByCode: (contentCode: number) => Promise<void>;
}

/** Опции загрузки состава ВЗН УП. */
interface UseStockFinishedGoodsReceiptContentsQueryOptions {
    /** Выполнять ли refetch при монтировании observer-а для stale-данных. */
    refetchOnMount?: boolean;
}

/**
 * Загружает строки ВЗН УП для размещения готовой продукции.
 * @param inplantCode код ВЗН УП
 * @param divisionCode код склада-получателя СГП (для рекомендаций и доступных ячеек)
 * @param options опции React Query observer
 */
export function useStockFinishedGoodsReceiptContentsQuery(
    inplantCode: number,
    divisionCode: number,
    options?: UseStockFinishedGoodsReceiptContentsQueryOptions,
): UseStockFinishedGoodsReceiptContentsQueryResult {
    const query = useQueryData<FinishedGoodsReceiptContent[]>({
        queryKey: getStockFinishedGoodsReceiptContentsQueryKey(inplantCode, divisionCode),
        queryFn: ({ signal }) =>
            finishedGoodsReceiptsServices.content.loadForReceipt(
                {
                    flt: { WsInplantCode: inplantCode },
                    divisionCode,
                    ...FINISHED_GOODS_RECEIPT_CONTENT_INCLUDES,
                },
                { signal },
            ),
        enabled: inplantCode > 0 && divisionCode > 0,
        refetchOnMount: options?.refetchOnMount,
        meta: { errorSource: "stock.finishedGoodsReceipt.contents" },
    });

    const refetchContentByCode = async (contentCode: number): Promise<void> => {
        await reloadAndReplaceCachedListItem({
            setData: query.setData,
            getKey: byCode,
            loadItem: async () => {
                const items = await finishedGoodsReceiptsServices.content.loadForReceipt({
                    flt: { WsInplantCode: inplantCode, Codes: [contentCode] },
                    divisionCode,
                    ...FINISHED_GOODS_RECEIPT_CONTENT_INCLUDES,
                });
                return items[0] ?? null;
            },
        });
    };

    return {
        content: query.data ?? [],
        isFetching: query.isFetching,
        refetch: query.refetch,
        refetchContentByCode,
    };
}
